import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getContentsList, deleteContent } from "../../../services/contentsService";
import { paginateData, calculateTotalPages } from "../../../assets/js/script";
import TableHeader from "../../Common/TableComponent/TableHeader";
import TableToolbar from "../../Common/TableComponent/TableToolbar";
import TablesRow from "../../Common/TableComponent/TablesRow";
import { renderStatusBadge } from "../../Common/TableComponent/tableBadges";
import { Pagination } from "../../Common/TableComponent/Pagination";
import { Loading } from "../../Common/OtherElements/Loading";
import Swal from "sweetalert2";
import { confirmDelete } from "../../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import ViewDescriptionModal from "./ViewDescriptionModal";
import allImages from "../../../assets/images-import";

const getContentId = (item) => item.id ?? item.Id;

const getTitle = (item) => item.title || item.Title || "-";

const getCategoryName = (item) =>
  item.categoryName ||
  item.CategoryName ||
  item.contentCategoryName ||
  item.ContentCategoryName ||
  "-";

const getTopicName = (item) =>
  item.topicName ||
  item.TopicName ||
  item.contentTopicName ||
  item.ContentTopicName ||
  "-";

const getImageUrl = (item) =>
  item.contentImageUrl ||
  item.ContentImageUrl ||
  item.thumbnailUrl ||
  item.ThumbnailUrl ||
  "";

const getPublishedOn = (item) => {
  const raw = item.publishedOn || item.PublishedOn;
  if (!raw) return "-";
  const date = new Date(raw);
  return Number.isNaN(date.getTime())
    ? String(raw)
    : date.toLocaleString();
};

const isTruthyFlag = (item, camelKey, pascalKey) => {
  const value = item[camelKey] ?? item[pascalKey];
  return value === true || value === "true" || value === 1;
};

const renderFlagBadge = (isOn, onLabel, offLabel) => (
  <span className={`badge badge-soft-${isOn ? "success" : "secondary"}`}>
    {isOn ? onLabel : offLabel}
  </span>
); 

const renderThumbnail = (url) => (
  <img
    src={url || allImages.DefultImage}
    alt="Content"
    className="rounded"
    style={{ width: 48, height: 48, objectFit: "cover" }}
    onError={(e) => {
      e.target.src = allImages.DefultImage;
    }}
  />
);

export const ManageContents = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "contents";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [contentsList, setContentsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewModal, setViewModal] = useState({
    show: false,
    title: "",
    html: "",
  });

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getContentsList();
      const list = response.data?.result || response.data || [];
      setContentsList(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = contentsList.filter((item) => {
    const title = getTitle(item).toLowerCase();
    const category = getCategoryName(item).toLowerCase();
    const topic = getTopicName(item).toLowerCase();
    const query = searchQuery.toLowerCase();
    return (
      title.includes(query) ||
      category.includes(query) ||
      topic.includes(query)
    );
  });

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (contentId) => {
    const confirmed = await confirmDelete("Content");
    if (confirmed) {
      try {
        await deleteContent(contentId);
        Swal.fire(
          "Deleted!",
          "The content has been deleted successfully.",
          "success"
        );
        fetchData();
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  const handleViewDescription = (item) => {
    const html =
      item.fullDescription ||
      item.FullDescription ||
      item.shortDescription ||
      item.ShortDescription ||
      "";
    setViewModal({
      show: true,
      title: getTitle(item),
      html,
    });
  };

  return (
    <>
      <ComponentHeader title="Contents" />

      <ViewDescriptionModal
        show={viewModal.show}
        onHide={() => setViewModal({ show: false, title: "", html: "" })}
        title={viewModal.title}
        htmlContent={viewModal.html}
      />

      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h5 className="mb-0">Manage Contents</h5>
                {pageAccessDetails.addAccess ? (
                  <Link
                    to="/contents/add"
                    className="btn btn-secondary btn-sm"
                  >
                    <i className="ri-add-line align-middle me-1"></i>
                    Add Content
                  </Link>
                ) : null}
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <TableToolbar
                  entriesPerPage={entriesPerPage}
                  onEntriesChange={handleEntriesChange}
                  searchValue={searchQuery}
                  searchPlaceholder="Search..."
                  onSearchChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "Sl No",
                          "Image",
                          "Title",
                          "Category",
                          "Topic",
                          "Featured",
                          "Premium",
                          "Published On",
                          "Status",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {currentData.length === 0 ? (
                          <TableDataStatusError colspan="10" />
                        ) : (
                          currentData.map((item, index) => {
                            const isFeatured = isTruthyFlag(
                              item,
                              "isFeatured",
                              "IsFeatured"
                            );
                            const isPremium = isTruthyFlag(
                              item,
                              "isPremium",
                              "IsPremium"
                            );

                            return (
                              <TablesRow
                                key={getContentId(item)}
                                rowData={{
                                  slNo:
                                    (currentPage - 1) * entriesPerPage +
                                    index +
                                    1,
                                  image: renderThumbnail(getImageUrl(item)),
                                  title: getTitle(item),
                                  category: getCategoryName(item),
                                  topic: getTopicName(item),
                                  featured: renderFlagBadge(
                                    isFeatured,
                                    "Yes",
                                    "No"
                                  ),
                                  premium: renderFlagBadge(
                                    isPremium,
                                    "Premium",
                                    "Standard"
                                  ),
                                  publishedOn: getPublishedOn(item),
                                  status: renderStatusBadge(item),
                                }}
                                columns={[
                                  "slNo",
                                  "image",
                                  "title",
                                  "category",
                                  "topic",
                                  "featured",
                                  "premium",
                                  "publishedOn",
                                  "status",
                                ]}
                                hideIcons={false}
                                onView={() => handleViewDescription(item)}
                                onEdit={() => {
                                  navigate(
                                    `/contents/update/${getContentId(item)}`
                                  );
                                }}
                                onDelete={() =>
                                  handleDelete(getContentId(item))
                                }
                                pageLevelAccessData={pageAccessDetails}
                              />
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
